import React from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, XCircle, Info } from 'lucide-react';
import type { ScanResult, GuardrailMatch } from '@/api/features/guardrails';

interface GuardrailWarningModalProps {
    isOpen: boolean;
    scanResult: ScanResult;
    onClose: () => void; // "Edit Message" - close modal, keep message
    onSendAnyway: () => void; // "Send Anyway" - proceed with send
    isSending?: boolean; // Disable "Send Anyway" during active send to prevent double-fire
}

const SeverityIcon: React.FC<{ severity: 'HIGH' | 'MEDIUM' | 'LOW'; size?: number }> = ({
    severity,
    size = 16
}) => {
    switch (severity) {
        case 'HIGH':
            return <XCircle size={size} className="text-red-500 dark:text-red-300" />;
        case 'MEDIUM':
            return <AlertTriangle size={size} className="text-purple-500 dark:text-purple-300" />;
        case 'LOW':
            return <Info size={size} className="text-blue-500 dark:text-blue-300" />;
    }
};

const SeverityBadge: React.FC<{ severity: 'HIGH' | 'MEDIUM' | 'LOW' }> = ({ severity }) => {
    const colorClasses = {
        HIGH: 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-100',
        MEDIUM: 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-100',
        LOW: 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100'
    };

    return (
        <span className={`px-2 py-0.5 text-xs font-medium rounded ${colorClasses[severity]}`}>
            {severity}
        </span>
    );
};

const MatchItem: React.FC<{ match: GuardrailMatch }> = ({ match }) => {
    return (
        <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="shrink-0 mt-0.5">
                <SeverityIcon severity={match.severity} size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800 dark:text-white">
                        &quot;{match.matchedText}&quot;
                    </span>
                    <SeverityBadge severity={match.severity} />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-300 mb-1">
                    {match.riskCategory}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-100">
                    {match.warningReason}
                </p>
            </div>
        </div>
    );
};

export const GuardrailWarningModal: React.FC<GuardrailWarningModalProps> = ({
    isOpen,
    scanResult,
    onClose,
    onSendAnyway,
    isSending = false
}) => {
    if (!isOpen) return null;

    const { matches, highCount, mediumCount, lowCount } = scanResult;

    const content = (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 1300 }}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900 rounded-t-lg">
                    <AlertTriangle size={20} className="text-amber-600 dark:text-amber-300" />
                    <h2 className="text-base font-semibold text-slate-800 dark:text-white">
                        Message Review Required
                    </h2>
                </div>

                {/* Summary */}
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-slate-600 dark:text-slate-200 mb-2">
                        Your message contains phrases that may require careful consideration before sending:
                    </p>
                    <div className="flex gap-4 text-sm">
                        {highCount > 0 && (
                            <div className="flex items-center gap-1">
                                <SeverityIcon severity="HIGH" size={14} />
                                <span className="text-red-600 dark:text-red-400 font-medium">
                                    {highCount} High
                                </span>
                            </div>
                        )}
                        {mediumCount > 0 && (
                            <div className="flex items-center gap-1">
                                <SeverityIcon severity="MEDIUM" size={14} />
                                <span className="text-purple-600 dark:text-purple-400 font-medium">
                                    {mediumCount} Medium
                                </span>
                            </div>
                        )}
                        {lowCount > 0 && (
                            <div className="flex items-center gap-1">
                                <SeverityIcon severity="LOW" size={14} />
                                <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    {lowCount} Low
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Matches List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50 dark:bg-slate-800">
                    {matches.map((match) => (
                        <MatchItem key={match.id} match={match} />
                    ))}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-b-lg">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                        Edit Message
                    </button>
                    <button
                        onClick={onSendAnyway}
                        disabled={isSending}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSending ? 'Sending...' : 'Send Anyway'}
                    </button>
                </div>
            </div>
        </div>
    );

    if (typeof document !== 'undefined') {
        return ReactDOM.createPortal(content, document.body);
    }

    return null;
};

export default GuardrailWarningModal;
